import { videoPlaybackCoordinator } from '@theme/video-playback-coordinator';

const YOUTUBE_TOGGLE_SELECTOR = '[data-youtube-audio-toggle]';
const YOUTUBE_IFRAME_SELECTOR = '[data-youtube-video-iframe]';
const SLIDE_SELECTOR = 'slideshow-slide';
const MOBILE_VIDEO_TEXT_SLIDE_SELECTOR = 'slideshow-slide.slide--mobile-video-text';
const MOBILE_VIDEO_TEXT_PLAYING_CLASS = 'slide--mobile-video-text-playing';
const mobileVideoQuery = window.matchMedia('(max-width: 749px)');
const mobileTabletVideoQuery = window.matchMedia('(max-width: 1199px)');

/**
 * Send a command to a YouTube iframe player.
 * @param {HTMLIFrameElement | null} iframe
 * @param {string} func
 */
function postYouTubeCommand(iframe, func) {
  if (!(iframe instanceof HTMLIFrameElement)) return;
  videoPlaybackCoordinator.command(iframe, func);
}

/**
 * @param {Element | null} slide
 * @returns {HTMLIFrameElement | null}
 */
function getSlideIframe(slide) {
  if (!(slide instanceof Element)) return null;
  const iframe = slide.querySelector(YOUTUBE_IFRAME_SELECTOR);
  return iframe instanceof HTMLIFrameElement ? iframe : null;
}

/**
 * @param {Element | null} slide
 * @returns {HTMLButtonElement | null}
 */
function getSlideToggleButton(slide) {
  if (!(slide instanceof Element)) return null;
  const button = slide.querySelector(YOUTUBE_TOGGLE_SELECTOR);
  return button instanceof HTMLButtonElement ? button : null;
}

/**
 * Update toggle UI state.
 * @param {HTMLButtonElement} button
 * @param {boolean} muted
 */
function updateToggleState(button, muted) {
  button.dataset.muted = muted ? 'true' : 'false';
  button.setAttribute('aria-pressed', muted ? 'false' : 'true');
  button.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
}

/**
 * Preserve audible desktop playback, but start the opted-in slide muted
 * on mobile and tablet so Safari can use muted autoplay and the button reflects
 * the actual initial iframe mute state.
 * @param {Element | null} slide
 * @param {HTMLButtonElement | null} button
 * @returns {boolean}
 */
function shouldMuteOnSlideStart(slide, button) {
  if (slide instanceof Element && slide.matches(MOBILE_VIDEO_TEXT_SLIDE_SELECTOR)) {
    return mobileTabletVideoQuery.matches;
  }
  return button ? button.dataset.muted !== 'false' : false;
}

/**
 * Pause and mute a YouTube slide.
 * @param {Element} slide
 */
function pauseAndMuteSlide(slide) {
  const iframe = getSlideIframe(slide);
  if (!iframe) return;

  videoPlaybackCoordinator.pause(iframe);
  postYouTubeCommand(iframe, 'mute');

  const button = getSlideToggleButton(slide);
  if (button) {
    updateToggleState(button, true);
  }
}

/**
 * Apply current toggle state to a selected slide and ensure playback is resumed.
 * @param {Element} slide
 */
function syncSelectedSlideAudio(slide) {
  const iframe = getSlideIframe(slide);
  if (!iframe) return;

  const button = getSlideToggleButton(slide);
  const muted = shouldMuteOnSlideStart(slide, button);

  videoPlaybackCoordinator.play(iframe);
  postYouTubeCommand(iframe, muted ? 'mute' : 'unMute');

  if (button) {
    updateToggleState(button, muted);
  }
}

/**
 * Initialize one toggle button.
 * @param {HTMLButtonElement} button
 */
function initializeToggle(button) {
  if (button.dataset.youtubeAudioInitialized === 'true') return;
  button.dataset.youtubeAudioInitialized = 'true';

  const slide = button.closest(SLIDE_SELECTOR);
  updateToggleState(button, shouldMuteOnSlideStart(slide, button));

  const stopSlideSelection = (event) => {
    event.stopPropagation();
  };

  // Slideshow drag handling starts on mousedown, so block it at the toggle.
  button.addEventListener('mousedown', stopSlideSelection);
  button.addEventListener('pointerdown', stopSlideSelection);
  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();

    const slide = button.closest(SLIDE_SELECTOR);
    if (!(slide instanceof Element)) return;

    const iframe = getSlideIframe(slide);
    if (!iframe) return;

    const nextMuted = button.dataset.muted === 'false';

    videoPlaybackCoordinator.play(iframe);
    postYouTubeCommand(iframe, nextMuted ? 'mute' : 'unMute');
    updateToggleState(button, nextMuted);
  });
}

/**
 * Stop the active overlay run when leaving a slide, but keep the "shown"
 * marker so the caption cannot replay after the user returns.
 * @param {Element} slide
 */
function resetMobileVideoText(slide) {
  slide.classList.remove(MOBILE_VIDEO_TEXT_PLAYING_CLASS);
  delete slide.dataset.mobileVideoTextPlayed;
}

/**
 * Subscribe to reports from the existing YouTube iframe.
 * This uses its current IFrame API messaging channel instead of recreating it.
 * @param {HTMLIFrameElement | null} iframe
 */
function subscribeToMobileVideoState(iframe) {
  if (iframe instanceof HTMLIFrameElement) videoPlaybackCoordinator.register(iframe);
}

/**
 * Allow direct YouTube interaction on mobile and tablet.
 * Keep mobile playback-text state independent of iframe accessibility.
 * @param {Element} slide
 */
function syncMobileVideoAccessibility(slide) {
  const iframe = getSlideIframe(slide);
  if (!iframe) return;

  if (mobileTabletVideoQuery.matches) {
    iframe.removeAttribute('aria-hidden');
    iframe.removeAttribute('tabindex');
  } else {
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');
  }

  if (!mobileVideoQuery.matches) resetMobileVideoText(slide);
}

/**
 * Initialize only slides explicitly opted in via Shopify Liquid.
 * @param {ParentNode} root
 */
function initializeMobileVideoText(root = document) {
  root.querySelectorAll(MOBILE_VIDEO_TEXT_SLIDE_SELECTOR).forEach((slide) => {
    if (!(slide instanceof Element)) return;
    const iframe = getSlideIframe(slide);
    if (!iframe) return;

    syncMobileVideoAccessibility(slide);
    subscribeToMobileVideoState(iframe);
  });
}

/**
 * Count an overlay as shown only when its delayed CSS reveal actually begins.
 * Leaving the video slide before that event allows the reveal on a later visit.
 */
document.addEventListener('animationstart', (event) => {
  if (!mobileVideoQuery.matches || event.animationName !== 'slide-text-overlay-mobile-reveal') return;

  const textBlock = event.target;
  if (!(textBlock instanceof Element)) return;
  if (!textBlock.matches('.slide__content--mobile-video-text .slide__content-layout .text-block')) return;

  const slide = textBlock.closest(MOBILE_VIDEO_TEXT_SLIDE_SELECTOR);
  if (!(slide instanceof Element)) return;
  if (slide.getAttribute('aria-hidden') !== 'false' || !slide.classList.contains(MOBILE_VIDEO_TEXT_PLAYING_CLASS)) return;

  slide.dataset.mobileVideoTextShown = 'true';
});

// A YouTube playerState of 1 indicates actual playback, not a Play request.
window.addEventListener('message', (event) => {
  if (!mobileVideoQuery.matches) return;
  if (event.origin !== 'https://www.youtube.com' && event.origin !== 'https://www.youtube-nocookie.com') return;

  let message;
  try {
    message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
  } catch {
    return;
  }
  if (!message || typeof message !== 'object') return;

  const playingState = message.event === 'infoDelivery'
    ? message.info?.playerState
    : message.event === 'onStateChange' ? message.info : undefined;
  if (playingState !== 1) return;

  for (const slide of document.querySelectorAll(MOBILE_VIDEO_TEXT_SLIDE_SELECTOR)) {
    const iframe = getSlideIframe(slide);
    if (iframe?.contentWindow !== event.source || new URL(iframe.src, location.href).origin !== event.origin || slide.getAttribute('aria-hidden') !== 'false') continue;
    if (slide.dataset.mobileVideoTextShown === 'true' || slide.dataset.mobileVideoTextPlayed === 'true') return;

    slide.dataset.mobileVideoTextPlayed = 'true';
    slide.classList.add(MOBILE_VIDEO_TEXT_PLAYING_CLASS);
    return;
  }
});

mobileTabletVideoQuery.addEventListener('change', () => {
  document.querySelectorAll(MOBILE_VIDEO_TEXT_SLIDE_SELECTOR).forEach((slide) => {
    syncMobileVideoAccessibility(slide);
    if (slide instanceof Element && slide.getAttribute('aria-hidden') === 'false') {
      syncSelectedSlideAudio(slide);
    }
  });
});

mobileVideoQuery.addEventListener('change', () => {
  document.querySelectorAll(MOBILE_VIDEO_TEXT_SLIDE_SELECTOR).forEach((slide) => {
    if (!(slide instanceof Element)) return;
    syncMobileVideoAccessibility(slide);
    if (mobileVideoQuery.matches) subscribeToMobileVideoState(getSlideIframe(slide));
    if (slide.getAttribute('aria-hidden') === 'false') syncSelectedSlideAudio(slide);
  });
});

/**
 * Initialize toggles and default audio state in a context.
 * @param {ParentNode} root
 */
function initializeYouTubeAudio(root = document) {
  initializeMobileVideoText(root);
  root.querySelectorAll(YOUTUBE_TOGGLE_SELECTOR).forEach((button) => {
    if (button instanceof HTMLButtonElement) {
      initializeToggle(button);
    }
  });

  root.querySelectorAll(SLIDE_SELECTOR).forEach((slide) => {
    if (!(slide instanceof Element) || !getSlideIframe(slide)) return;

    const active = slide.getAttribute('aria-hidden') === 'false';
    if (active) {
      syncSelectedSlideAudio(slide);
    } else {
      pauseAndMuteSlide(slide);
    }
  });
}

document.addEventListener('slideshow:select', (event) => {
  const selectedSlide = event?.detail?.slide;
  if (!(selectedSlide instanceof Element)) return;

  const slideshow = selectedSlide.closest('slideshow-component');
  if (!(slideshow instanceof Element)) return;

  slideshow.querySelectorAll(SLIDE_SELECTOR).forEach((slide) => {
    if (!(slide instanceof Element)) return;
    if (slide !== selectedSlide && slide.matches(MOBILE_VIDEO_TEXT_SLIDE_SELECTOR)) {
      resetMobileVideoText(slide);
    }
    if (!getSlideIframe(slide)) return;

    if (slide === selectedSlide) {
      syncSelectedSlideAudio(slide);
    } else {
      pauseAndMuteSlide(slide);
    }
  });
});

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initializeYouTubeAudio();
    },
    { once: true }
  );
} else {
  initializeYouTubeAudio();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    initializeYouTubeAudio(event.target);
  }
});
